<template>
  <div class="activity-stage">
    <div class="hearittypeit-container">
      <div class="hit-instructions">
        {{ currentRound.instructions }}
      </div>
      <div class="content" v-if="showContent" transition="fade-05">
        <div class="left">
          <img alt="" v-if="currentRound.image" :src="currentRound.image" />
        </div>
        <div class="right">
          <div v-for="(attempt, idx) in attempts" class="attempt" :key="attempt.key">
            <div
              class="attempt-line"
              :id="`attempt-${idx}`"
              :transition="transition"
              v-if="idx < attempts.length - 1 && (attempt.blinking || attempt.isCompleted)"
            >
              <div class="attempt-box">
                <div class="box-wrapper">
                  <p :class="['box-text', { blinking: attempt.blinking }]">
                    <span
                      aria-hidden="true"
                      :key="jdx + letter"
                      v-for="(letter, jdx) in attempt.text"
                      v-html="letter"
                    ></span>
                  </p>
                </div>
              </div>
              <done-button
                v-if="attempt.text && attempt.text.length > 0"
                :class="[attempt.blinking ? '' : 'hidden']"
                :on-click="onDone"
                :transition="innerTransition"
                text="Done"
              >
              </done-button>
            </div>

            <div
              :transition="transition"
              v-if="idx === attempts.length - 1 && (attempt.blinking || attempt.isCompleted)"
            >
              <div class="attempt-box blinking-text">
                <blinking-cursor-text
                  :round-id="currentRound.id"
                  :class-name="'box-text'"
                  :text="currentRound.steps[0].answer"
                  :text-step="0"
                >
                </blinking-cursor-text>
              </div>
            </div>
          </div>

          <div
            v-if="showBox"
            class="did-you-hear-box"
            :transition="transition"
            @transitionend="onTryEnd"
          >
            <div class="hear-text">
              <p class="hear-text">
                {{ hearText }}
              </p>
            </div>
            <img :src="didYouHear" alt="" />
          </div>
          <div v-if="tryAgain" class="try-again" @click="onTryAgain" :transition="innerTransition">
            <img :src="tryAgainButton" alt="" />
            <span>Try Again</span>
          </div>
        </div>
      </div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax } from 'gsap';
import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';
import DoneButton from 'components/activity/DoneButton.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import tryAgainButton from 'assets/images/activities/hear-it-type-it/tryAgainButton.png';
import didYouHear from 'assets/images/activities/hear-it-type-it/didYouHear.png';

const TIMEOUT = 30000;
const TRY_TIMEOUT = 3000;

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    DoneButton,
    BlinkingCursorText
  },

  mounted() {
    this.keepInstructionsOnScreen(true);
    this.setFreeTextTyping(true);
  },

  data() {
    return {
      attempts: [],
      currentAttempt: 0,
      timeout: null,
      showBox: false,
      tryAgain: false,
      freeTextTyping: true,
      transition: 'fade-10',
      sounds: null,
      activeSound: null,
      showContent: false,
      tryAgainTimer: null,
      innerTransition: 'fade-10',
      hearText: '',
      tryAgainButton,
      didYouHear
    };
  },

  methods: {
    onTryEnd() {
      if (this.showDelayedTryAgain) {
        this.markCompleted();
        this.showDelayedTryAgain = false;
        this.tryAgain = true;
        this.disableKeyboard();
      }
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard || {};

      return {
        keyboardType: keyboardType || 'Keyboard35',
        roofs
      };
    },

    async loadActivity() {
      const activityState = this.getActivityState();
      this.createRounds(activityState);
      await this.loadImages(activityState);

      const sounds = [];
      activityState.rounds.forEach(({ id, audio, instructionsAudio }) => {
        sounds.push({ roundId: id, audio });
        sounds.push({ roundId: id, audio: instructionsAudio });
      });
      this.loadSounds(sounds);
      this.createAttemptsForCurrentRound(activityState.rounds[0]);
    },

    async loadSounds(sounds) {
      this.queueAudio(sounds.map(snd => snd.audio));
      const cache = await this.downloadAudio();
      this.sounds = sounds.map(sound => ({
        id: sound.roundId,
        audio: cache.get(sound.audio)
      }));
    },

    createAttemptsForCurrentRound({ attempts: numberOfAttempts }) {
      const attempts = [];

      for (let idx = 0; idx < numberOfAttempts + 1; idx += 1) {
        attempts.push({
          text: '',
          isCompleted: false,
          blinking: idx === 0,
          key: idx
        });
      }

      this.attempts = attempts;
    },

    setTimer() {
      this.showBox = false;
      clearTimeout(this.tryAgainTimer);
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.showBox = true;
        // do not set timer for last attempt
        if (this.currentAttempt === this.attempts.length - 1) return;
        this.tryAgainTimer = setTimeout(() => {
          this.showDelayedTryAgain = true;
          this.showBox = false;
        }, TRY_TIMEOUT);
      }, TIMEOUT);
    },

    playAudioForRound(skipInstructions = false) {
      this.fadeInInstructions();
      this.setHearText();
      this.transition = 'fade-10';
      this.showContent = true;

      if (this.activeSound) {
        this.activeSound.stop();
      }

      const playInstructionsAndSound = () => {
        const sound = this.sounds.filter(snd => snd.id === this.currentRound.id);
        const { audio: instructionsAudio } = sound[1];
        const { audio } = sound[0];

        if (skipInstructions) {
          audio.play();
          this.activeSound = audio;
          return;
        }

        this.activeSound = instructionsAudio;
        instructionsAudio.play();
        const playCurrent = () => {
          instructionsAudio.off('stop', playCurrent);
          instructionsAudio.off('end', playCurrent);
          audio.play();
          this.activeSound = audio;

          // fade in new round
          TweenMax.delayedCall(0.5, () => {
            // timer for keyboard inactivity
            this.setTimer();
            this.enableKeyboard();
          });
        };

        instructionsAudio.on('stop', playCurrent);
        instructionsAudio.on('end', playCurrent);
      };

      playInstructionsAndSound();
    },

    startRound() {
      this.disableKeyboard();
      this.setupNewRound();
      this.setHearText();
      this.fadeInInstructions();
      this.setFreeTextTyping(true);
      this.createAttemptsForCurrentRound(this.currentRound);
      // play audio
      this.playAudioForRound();
      // reset values
      this.showBox = false;
      this.tryAgain = false;
      this.currentAttempt = 0;
      this.freeTextTyping = true;
    },

    startNewRound() {
      if (this.isEndOfActivity) {
        this.keepInstructionsOnScreen(false);
        this.completeActivity();
        return;
      }

      this.timeout = null;
      this.transition = '';
      this.fadeOutInstructions();

      if (this.activeSound) {
        this.activeSound.stop();
      }

      const playSounds = () => {
        const congrats = this.playCongrats();
        const playAudio = () => {
          congrats.off('stop', playAudio);
          congrats.off('end', playAudio);
          this.showContent = false;
          TweenMax.delayedCall(0.5, this.startRound);
        };
        congrats.on('stop', playAudio);
        congrats.on('end', playAudio);
      };

      playSounds();
    },

    onTryAgain() {
      this.enableKeyboard();
      this.playAudioForRound(true);
      const lastFinishedList = this.attempts.filter(q => q.isCompleted);
      const lastFinished = lastFinishedList[lastFinishedList.length - 1].key;

      this.innerTransition = '';
      this.$nextTick(() => {
        // start blinking on attempt
        this.attempts = this.attempts.map(attempt =>
          attempt.key !== lastFinished + 1
            ? attempt
            : {
                ...attempt,
                blinking: true
              }
        );

        this.tryAgain = false;
        this.currentAttempt += 1;
        this.showDelayedTryAgain = false;

        if (this.currentAttempt === this.attempts.length - 1) {
          this.freeTextTyping = false;
          this.setFreeTextTyping(false);
        } else {
          this.setTimer();
        }

        this.$nextTick(() => {
          this.innerTransition = 'fade-10';
        });
      });
    },

    markCompleted() {
      const notFinished = this.attempts.filter(q => !q.isCompleted);
      const firstKey = notFinished[0].key;

      // mark activity as completed
      this.attempts = this.attempts.map(attempt =>
        attempt.key !== firstKey
          ? attempt
          : {
              ...attempt,
              isCompleted: true,
              blinking: false
            }
      );
    },

    setHearText() {
      const { type } = this.currentRound;
      const { answer } = this.currentStep;
      switch (type) {
        case 'letter':
          this.hearText = `Did you hear the letter${answer.trim().length > 1 ? 's' : ''}?`;
          return;
        case 'word':
          this.hearText = 'Did you hear the word?';
          return;
        default:
        case 'sentence':
          this.hearText = 'Did you hear the sentence?';
      }
    },

    onDone() {
      this.showDelayedTryAgain = false;
      const { type } = this.currentRound;
      const { answer } = this.currentStep;
      const { text } = this.attempts[this.currentAttempt];

      let answeredProperly = false;

      switch (type) {
        case 'letter': {
          const trimmedText = text.toLowerCase().replace(/ /g, '');
          const trimmedPhrase = answer.toLowerCase().replace(/ /g, '');
          answeredProperly = trimmedText === trimmedPhrase;
          break;
        }
        case 'word': {
          const lowerText = text.toLowerCase().trim();
          const lowerPhrase = answer.toLowerCase().trim();
          answeredProperly = lowerText === lowerPhrase;
          break;
        }
        case 'sentence':
        default: {
          answeredProperly = text === answer;
          break;
        }
      }

      this.markCompleted();
      if (answeredProperly) {
        this.startNewRound();
        return;
      }

      this.tryAgain = true;
      this.disableKeyboard();
    },

    setContainerHeight() {
      const keyboard = document.querySelector('.phoenix-keyboard-wrapper');
      const container = document.querySelector('.hearittypeit-container');
      container.style.height = `calc(100% - ${keyboard.offsetHeight + 70}px)`;
    },

    async loadImages({ rounds }) {
      rounds.forEach(({ image }) => this.queueImage(image));
      this.downloadImages();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          delay: 0,
          voice: ['']
        }
      };
    },

    fadeInInstructions() {
      TweenMax.to('.hit-instructions', 1, { opacity: 1 });
    },

    fadeOutInstructions() {
      TweenMax.to('.hit-instructions', 1, { opacity: 0 });
    },

    startActivity() {
      this.setupNewRound();
      this.errorLoggerStart();
      this.$nextTick(() => {
        this.setContainerHeight();
        this.playAudioForRound();
        // this.setTimer();
        // this.playAudioForRound();
      });
    },

    handleCorrectKey({ character }) {
      this.showDelayedTryAgain = false;
      this.showBox = false;
      clearTimeout(this.tryAgainTimer);
      clearTimeout(this.timeout);

      if (!this.freeTextTyping) {
        this.sndType();
        this.incrementTypingIndex();

        if (this.isEndOfCurrentSentence) {
          this.completeStep();

          // activity got completed
          if (this.isEndOfActivity) {
            this.keepInstructionsOnScreen(false);
            this.completeActivity();
            return;
          }

          this.setCurrentStep();
          if (this.currentAttempt === this.attempts.length - 1) {
            this.startNewRound();
          }
        }

        return;
      }

      this.sndType();
      const lowerCh = character.toLowerCase();
      const noActionKeys = ['tab'];
      const { currentAttempt } = this;
      const currentText = this.attempts[currentAttempt].text;

      if (lowerCh === 'enter') {
        // enter acts the same as done button
        this.disableKeyboard();
        this.onDone();
        return;
      }

      if (currentText.length === 200) return;
      if (noActionKeys.includes(lowerCh)) return;
      if (lowerCh === 'backspace') {
        const end = currentText.length === 0 ? 0 : currentText.length - 1;
        this.attempts[currentAttempt].text = this.attempts[currentAttempt].text.substring(0, end);
      } else {
        this.attempts[currentAttempt].text = `${currentText}${character}`;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/fade.scss';

.hidden {
  visibility: hidden;
}

.hearittypeit-container {
  margin-top: 70px;
}

.content {
  display: flex;

  .left {
    flex-basis: 35%;
    padding-left: 10px;
    padding-right: 10px;

    img {
      max-width: 100%;
      height: auto;
    }
  }
  .right {
    flex-basis: 65%;
    padding-left: 10px;
    padding-right: 40px;
  }
}

.attempt {
  margin-top: 10px;
}

.checkmark-icon {
  margin-left: 10px;
}

.attempt-box,
.checkmark-icon {
  vertical-align: middle;
  display: inline-block;
}

.box-wrapper {
  width: 2000px;
}

.attempt-box {
  width: 475px;
  height: 56px;
  padding: 13px 12px;
  border: 2px solid #000;
  box-sizing: border-box;
  overflow: hidden;

  .box-text {
    display: inline-block;
    font-size: 20px;
    min-height: 30px;
    position: relative;
    margin: 0;
    span {
      color: #000;
    }

    .blinking-text {
      color: initial;
    }

    &.blinking::after {
      content: '';
      position: absolute;
      width: 10px;
      height: 0;
      right: -10px;
      bottom: 2px;
      border-bottom: 2px solid #000;
      animation: blinky 0.65s step-end infinite;
      animation-iteration-count: infinite;
    }
  }
}

.hear-text {
  top: 50%;
  left: 50%;
  z-index: 100;
  position: absolute;
  transform: translate(-50%, -50%);
  font-family: 'hwtfutura-bold';
  color: #000;
  width: 225px;
  p {
    text-align: center;
  }
}

.did-you-hear-box {
  position: relative;
  display: block;
  width: 475px;

  img {
    display: block;
    width: 270px;
    height: auto;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    z-index: 20;
  }
}

.try-again {
  width: 475px;
  text-align: center;
  cursor: pointer;
  img {
    display: inline-block;
    width: 90px;
    height: auto;
  }
  span {
    display: block;
  }
}

@keyframes blinky {
  from,
  to {
    border-bottom: 2px solid #000;
  }
  50% {
    border-bottom: 0px solid #000;
  }
}

.hit-instructions {
  opacity: 0;
  font-family: 'hwtfutura';
  margin: 10px 0;
  font-size: 18px;
  position: fixed;
  top: 0;
  left: 1.5%;
  z-index: 10;
  -webkit-font-smoothing: auto;
}

#activity-content {
  opacity: 0;
}
</style>
